import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Box,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Products = () => {
  const { user: currentUser } = useAuth();
  const [products, setProducts] = useState([]);
  const [modules, setModules] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    is_active: true,
    modules: [],
  });

  useEffect(() => {
    fetchProducts();
    fetchModules();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
    }
  };

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/modules`);
      setModules(response.data);
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
    }
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setSelectedProduct(product);
      setFormData({
        name: product.name,
        is_active: product.is_active,
        modules: product.modules?.map(m => m.id) || [],
      });
    } else {
      setSelectedProduct(null);
      setFormData({
        name: '',
        is_active: true,
        modules: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProduct(null);
  };

  const handleSubmit = async () => {
    try {
      if (selectedProduct) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/products/${selectedProduct.id}`,
          formData
        );
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/products`, formData);
      }
      fetchProducts();
      handleCloseDialog();
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
      alert('Ha ocurrido un error, por favor intenta más tarde nuevamente.');
    }
  };

  const handleDelete = async (productId) => {
    if (window.confirm('¿Seguro de eliminar este producto?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/products/${productId}`);
        fetchProducts();
      } catch (error) {
        console.error('Ha ocurrido un error:', error);
        alert('Ha ocurrido un error, por favor intenta más tarde nuevamente.');
      }
    }
  };

  const handleModuleChange = (moduleId) => {
    const newModules = formData.modules.includes(moduleId)
      ? formData.modules.filter(id => id !== moduleId)
      : [...formData.modules, moduleId];
    setFormData({ ...formData, modules: newModules });
  };

  const canModifyProduct = () => {
    return ['Administrador', 'Sistemas'].includes(currentUser.role);
  };

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" gutterBottom>
          Productos
        </Typography>
        {canModifyProduct() && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Agregar producto
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ whiteSpace: 'nowrap' }}>
              <TableCell>Nombre</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Módulos</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id} sx={{ whiteSpace: 'nowrap' }}>
                <TableCell>{product.name}</TableCell>
                <TableCell>
                  <Chip
                    label={product.is_active ? 'Activo' : 'Inactivo'}
                    color={product.is_active ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {product.modules?.map((module) => (
                    <Chip
                      key={module.id}
                      label={module.name}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </TableCell>
                <TableCell>
                  {canModifyProduct() && (
                    <>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenDialog(product)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDelete(product.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          {selectedProduct ? 'Editar producto' : 'Agregar producto'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <FormControl component="fieldset" margin="normal">
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                />
              }
              label="Activo"
            />
          </FormControl>
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Módulos
          </Typography>
          <FormGroup>
            {modules.map((module) => (
              <FormControlLabel
                key={module.id}
                control={
                  <Checkbox
                    checked={formData.modules.includes(module.id)}
                    onChange={() => handleModuleChange(module.id)}
                  />
                }
                label={`${module.name}`}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Products;